// 1. Importe os VALORES (funções, hooks) normalmente
import { useState, useEffect } from "react";
import { storage } from "../../utils/storage";
// 2. Importe os TIPOS usando a palavra-chave "type"
import type { ReactNode } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  TablePagination,
  CardContent,
  Card,
  Grid,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import type { PaginatedResponse } from "../../utils/api";

// --- SEÇÃO DE TIPOS E INTERFACES ---

// Definição para cada coluna da tabela
export interface ColumnDefinition<T> {
  header: string | ReactNode | ((data: T[]) => ReactNode);
  accessor: keyof T | ((item: T) => ReactNode);
  align?: "left" | "right" | "center";
}

// Definição para uma opção de filtro (ex: botão "Ativo")
export interface FilterOption {
  label: string;
  value: string;
}
// Props do componente ResourceList
interface ResourceListProps<T> {
  // fetchData agora recebe os parâmetros e retorna a resposta paginada
  fetchData: (params: {
    page: number;
    limit: number;
    search?: string;
  }) => Promise<PaginatedResponse<T>>;
  columns: ColumnDefinition<T>[];
  searchParam: string; // Nome do parâmetro de busca na URL (ex: 'search')
  resourceName?: string;
  customFilter?: any;
  // Novas props para navegação
  onRowClick?: (item: T) => void; // Função personalizada para click na linha
  navigateToPath?: (item: T) => string; // Função que retorna o path para navegação
  navigationMode?: "click" | "both" | "eye"; // Modo de navegação
  viewIconPosition?: "start" | "end"; // Posição do ícone de view
  // Controle externo de paginação
  externalPage?: number;
  onPageChange?: (event: unknown, newPage: number) => void;
  // Controle externo de busca
  initialSearch?: string;
  onSearchChange?: (search: string) => void;
  // localStorage key para persistir rowsPerPage
  storageKey?: string; // ex: 'plants-per-page', 'service-orders-per-page'
}

// --- FUNÇÕES HELPER ---

// Helper para acessar valores aninhados em um objeto (ex: 'client.name')
const getNestedValue = (obj: any, path: string): any => {
  return path.split(".").reduce((acc, part) => acc && acc[part], obj);
};

// --- O COMPONENTE ---

export default function ResourceList<T extends { _id: string | number }>({
  fetchData,
  columns,
  resourceName,
  customFilter,
  onRowClick,
  navigateToPath,
  navigationMode = "click",
  viewIconPosition = "end",
  externalPage,
  onPageChange: externalOnPageChange,
  initialSearch,
  onSearchChange,
  storageKey,
}: ResourceListProps<T>) {
  // --- ESTADOS DO COMPONENTE ---
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Estado para a paginação - usa externalPage como valor inicial se fornecido
  const [page, setPage] = useState(() => externalPage !== undefined ? externalPage : 0);
  
  // Inicializa rowsPerPage do localStorage se storageKey fornecido
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    if (storageKey) {
      const stored = storage.getResourceListRowsPerPage(storageKey);
      if (stored !== null && [5, 10, 25, 50, 100].includes(stored)) {
        return stored;
      }
    }
    return 10;
  });
  
  const [totalItems, setTotalItems] = useState(0);
  const [filterValue] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState(initialSearch || "");
  const [inputValue, setInputValue] = useState(initialSearch || ""); // ✅ Novo estado para o input
  
  // Usa página externa se fornecida
  const currentPage = externalPage !== undefined ? externalPage : page;

  // Sincroniza estado interno com externalPage quando fornecido
  useEffect(() => {
    if (externalPage !== undefined && page !== externalPage) {
      setPage(externalPage);
    }
  }, [externalPage, page]);

  useEffect(() => {
    // Cria um "timer"
    const handler = setTimeout(() => {
      setSearchTerm(inputValue); // Atualiza o termo de busca real após 500ms
      if (onSearchChange) {
        onSearchChange(inputValue); // Notifica o pai sobre a mudança
      }
    }, 500); // Delay de 500ms

    // Limpa o timer se o usuário digitar novamente
    return () => {
      clearTimeout(handler);
    };
  }, [inputValue, onSearchChange]); // Este efeito roda toda vez que o input muda

  // --- EFEITOS ---
  // useEffect direto para evitar problemas de dependência com useCallback
  useEffect(() => {
    const params = {
      page: currentPage + 1,
      limit: rowsPerPage,
      search: searchTerm || undefined,
      [customFilter?.filterParam ?? "filter"]:
        filterValue === "all" ? undefined : filterValue,
    };
    
    setLoading(true);
    
    // Garante um tempo mínimo de loading para evitar "piscadas"
    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 300));
    
    Promise.all([fetchData(params), minLoadingTime])
      .then(([response]) => {
        setItems(response.data);
        setTotalItems(response.pagination.total);
        
        // Se a página atual está fora do range, reseta para a última página válida
        const maxPage = Math.max(0, Math.ceil(response.pagination.total / rowsPerPage) - 1);
        if (currentPage > maxPage && externalOnPageChange) {
          externalOnPageChange(null, maxPage);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentPage, rowsPerPage, searchTerm, filterValue, customFilter?.filterParam, fetchData, externalOnPageChange]);
  // --- HOOKS PARA RESPONSIVIDADE ---
  const theme = useTheme();
  // Retorna true se a tela for menor que o breakpoint 'md' (padrão: 900px)
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleChangePage = (_event: unknown, newPage: number) => {
    if (externalOnPageChange) {
      externalOnPageChange(_event, newPage);
    } else {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    
    // Salva no localStorage se storageKey fornecido
    if (storageKey) {
      storage.setResourceListRowsPerPage(storageKey, newRowsPerPage);
    }
  };

  // --- FUNÇÕES DE NAVEGAÇÃO ---
  const handleRowClick = (item: T, event: React.MouseEvent) => {
    // Não navega se o modo for apenas "eye"
    if (navigationMode === "eye") {
      return;
    }

    // Previne navegação se clicou em botões/áreas protegidas
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('[data-no-navigate]')) {
      return;
    }

    if (onRowClick) {
      onRowClick(item);
    } else if (navigateToPath) {
      navigate(navigateToPath(item));
    }
  };

  const handleViewClick = (item: T, event: React.MouseEvent) => {
    event.stopPropagation(); // Evita que o clique na linha seja acionado
    if (onRowClick) {
      onRowClick(item);
    } else if (navigateToPath) {
      navigate(navigateToPath(item));
    }
  };

  // Determina se deve mostrar o ícone de view
  const shouldShowViewIcon = navigationMode === "both" || navigationMode === "eye";
  
  // Determina se a linha deve ser clicável
  const isRowClickable = (navigationMode === "click" || navigationMode === "both") && (onRowClick || navigateToPath);
  // --- FUNÇÕES DE RENDERIZAÇÃO CONDICIONAL ---

  // Função para renderizar a Tabela em telas maiores
  const renderTable = () => (
    <TableContainer>
      <Table
        stickyHeader
        sx={{ minWidth: 650 }}
        aria-label={`tabela de ${resourceName}`}
      >
        <TableHead>
          <TableRow>
            {/* Ícone de view no início */}
            {shouldShowViewIcon && viewIconPosition === "start" && (
              <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "#FAFAFA", width: 60 }}>
                Ações
              </TableCell>
            )}
            
            {columns.map((col, index) => (
              <TableCell
                key={`header-${index}`}
                align={col.align}
                sx={{ fontWeight: 600, bgcolor: "#FAFAFA" }}
              >
                {typeof col.header === 'function' ? col.header(items) : col.header}
              </TableCell>
            ))}
            
            {/* Ícone de view no final */}
            {shouldShowViewIcon && viewIconPosition === "end" && (
              <TableCell align="center" sx={{ fontWeight: 600, bgcolor: "#FAFAFA", width: 60 }}>
                Ações
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell 
                colSpan={columns.length + (shouldShowViewIcon ? 1 : 0)} 
                align="center"
                sx={{ py: 6 }}
              >
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell 
                colSpan={columns.length + (shouldShowViewIcon ? 1 : 0)} 
                align="center"
                sx={{ py: 6 }}
              >
                <Typography color="text.secondary">
                  Nenhum resultado encontrado
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => (
              <TableRow 
                key={item._id} 
                hover
                onClick={(event) => handleRowClick(item, event)}
                sx={{ 
                  cursor: isRowClickable ? 'pointer' : 'default',
                  '&:hover': {
                    backgroundColor: isRowClickable ? 'action.hover' : 'inherit'
                  }
                }}
              >
                {/* Ícone de view no início */}
                {shouldShowViewIcon && viewIconPosition === "start" && (
                  <TableCell align="center" data-no-navigate>
                    <Tooltip title="Visualizar detalhes">
                      <IconButton 
                        size="small" 
                        onClick={(event) => handleViewClick(item, event)}
                        color="primary"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}

                {columns.map((col, index) => (
                  <TableCell key={`${item._id}-${index}`} align={col.align}>
                    {typeof col.accessor === "function"
                      ? col.accessor(item)
                      : (getNestedValue(
                          item,
                          col.accessor as string
                        ) as ReactNode)}
                  </TableCell>
                ))}

                {/* Ícone de view no final */}
                {shouldShowViewIcon && viewIconPosition === "end" && (
                  <TableCell align="center" data-no-navigate>
                    <Tooltip title="Visualizar detalhes">
                      <IconButton 
                        size="small" 
                        onClick={(event) => handleViewClick(item, event)}
                        color="primary"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Função para renderizar os Cards em telas menores
  const renderCards = () => (
    <Grid container spacing={2}>
      {items.map((item) => (
        <Grid item xs={12} key={item._id}>
          <Card 
            variant="outlined"
            onClick={(event) => handleRowClick(item, event)}
            sx={{ 
              cursor: isRowClickable ? 'pointer' : 'default',
              '&:hover': {
                backgroundColor: isRowClickable ? 'action.hover' : 'inherit',
                boxShadow: isRowClickable ? 2 : 1
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <CardContent
              sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 2 }}
            >
              {/* Header com título e ícone de view (se habilitado) */}
              {columns.length > 0 && (
                <Box
                  sx={{
                    mb: 1.5,
                    borderBottom: 1,
                    borderColor: "divider",
                    pb: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{ wordBreak: "break-word", lineHeight: 1.3, flex: 1 }}
                  >
                    {typeof columns[0].accessor === "function"
                      ? columns[0].accessor(item)
                      : (getNestedValue(
                          item,
                          columns[0].accessor as string
                        ) as ReactNode)}
                  </Typography>
                  
                  {/* Ícone de view no card */}
                  {shouldShowViewIcon && (
                    <Box data-no-navigate>
                      <Tooltip title="Visualizar detalhes">
                        <IconButton 
                          size="small" 
                          onClick={(event) => handleViewClick(item, event)}
                          color="primary"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              )}

              {/* ✅ LÓGICA DE LAYOUT CORRIGIDA: VOLTANDO AO FLEXBOX */}
              {columns.map((column, index) => (
                <Box
                  key={`${item._id}-mobile-${index}`}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between", // Mantém o rótulo na esquerda e o valor na direita
                    alignItems: "center", // Centraliza verticalmente na mesma linha
                    gap: 2,
                  }}
                >
                  {/* Coluna do Rótulo */}
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color="text.secondary"
                    sx={{ flexShrink: 0 }} // Impede que o rótulo seja espremido
                  >
                    {typeof column.header === 'function' ? column.header(items) : column.header}:
                  </Typography>

                  {/* Coluna do Valor */}
                  <Box
                    sx={{
                      textAlign: "right",
                      // ✅ A MÁGICA ESTÁ AQUI:
                      minWidth: 0, // Permite que o contêiner do valor encolha
                    }}
                  >
                    {/* O valor em si pode ser um Typography ou qualquer outro componente.
                      Se o valor for um texto longo, ele deve ter estilos de corte.
                      A definição da coluna já faz isso:
                      <Typography sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {os.description}
                      </Typography>
                  */}
                    {typeof column.accessor === "function"
                      ? column.accessor(item)
                      : (getNestedValue(
                          item,
                          column.accessor as string
                        ) as ReactNode)}
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
  // --- RENDERIZAÇÃO ---
  return (
    <Box sx={{ flexGrow: 1, flexDirection: "column", height: "100%" }}>
      {/* Seção de Filtros e Busca */}
      <Box sx={{ p: 3, flexShrink: 0, borderBottom: "1px solid", borderColor: "divider" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {customFilter ?? customFilter}

          {/* Barra de Busca */}
          <TextField
            placeholder={`Buscar em ${resourceName}...`}
            variant="outlined"
            size="small"
            fullWidth
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "white",
              },
            }}
          />
        </Box>
      </Box>
      {/* Conteúdo Principal: Tabela ou Cards */}
      <Box sx={{ flexGrow: 1, overflow: "auto" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%", // Ocupa toda a altura do pai flexível
            }}
          >
            <CircularProgress />
          </Box>
        ) : // Renderiza a tabela ou os cards diretamente aqui
        isMobile ? (
          renderCards()
        ) : (
          renderTable()
        )}
      </Box>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        sx={{ flexShrink: 0, borderTop: "1px solid", borderColor: "divider" }}
        component="div"
        count={totalItems}
        rowsPerPage={rowsPerPage}
        page={totalItems === 0 ? 0 : Math.min(currentPage, Math.ceil(totalItems / rowsPerPage) - 1)}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Itens por página:"
      />
    </Box>
  );
}
